import { useEffect, useRef, useState } from 'react'
import { TIPOS_EVENTO } from '@du/shared'
import { BASE_API } from './api.js'

export type EventoSse = { tipo: string; em: string } & Record<string, unknown>

/**
 * Assina o fluxo de eventos do portal.
 *
 * EventSource, e não WebSocket: o fluxo é só de ida, reconecta sozinho e
 * retoma por Last-Event-ID. E zero polling — a cozinha não pode esperar o
 * próximo intervalo para ver que entrou pedido.
 *
 * A autenticação vai pelo cookie httpOnly `du_sse` (EventSource não permite
 * header Authorization); `withCredentials` é o que manda o cookie.
 */
export function useEventos(aoEvento: (e: EventoSse) => void) {
  const [conectado, setConectado] = useState(false)
  const guardado = useRef(aoEvento)
  guardado.current = aoEvento

  useEffect(() => {
    let fonte: EventSource | null = null
    let tentativa = 0
    let agendado: number | undefined
    let desmontado = false

    const abrir = () => {
      if (desmontado) return
      fonte = new EventSource(`${BASE_API}/v1/eventos`, { withCredentials: true })

      fonte.onopen = () => {
        tentativa = 0
        setConectado(true)
      }

      // O servidor manda o tipo no campo `event`; EventSource só dispara o
      // handler de um nome REGISTRADO — não existe curinga. Por isso a lista
      // vem de @du/shared, a mesma que tipa o publicador no servidor: tipo
      // novo lá aparece aqui sozinho, em vez de sumir calado.
      const tratar = (ev: MessageEvent) => {
        try {
          guardado.current(JSON.parse(ev.data) as EventoSse)
        } catch {
          /* quadro malformado não derruba o fluxo */
        }
      }
      for (const tipo of ['message', ...TIPOS_EVENTO]) {
        fonte.addEventListener(tipo, tratar as EventListener)
      }

      fonte.onerror = () => {
        setConectado(false)
        fonte?.close()
        // Backoff com teto de 30s: rede de lanchonete cai, e insistir a cada
        // 100ms só piora. O jitter evita todos os tablets voltarem juntos.
        const espera = Math.min(30_000, 1000 * 2 ** tentativa) * (0.7 + Math.random() * 0.6)
        tentativa += 1
        agendado = window.setTimeout(abrir, espera)
      }
    }

    abrir()
    return () => {
      desmontado = true
      window.clearTimeout(agendado)
      fonte?.close()
    }
  }, [])

  return { conectado }
}

/**
 * Som de pedido novo.
 *
 * O navegador bloqueia áudio até a pessoa interagir com a página — por isso o
 * portal pede "ativar som" no login em vez de tentar tocar e falhar calado.
 * O som é sintetizado com WebAudio: nenhum arquivo para carregar, e funciona
 * mesmo com a internet ruim.
 */
export function useSomPedido() {
  const ctx = useRef<AudioContext | null>(null)
  const [ligado, setLigado] = useState(false)

  const ativar = () => {
    if (!ctx.current) ctx.current = new AudioContext()
    void ctx.current.resume().then(() => setLigado(ctx.current?.state === 'running'))
    setLigado(true)
    tocar()
  }

  /**
   * Religa o som no primeiro clique em qualquer lugar do portal.
   *
   * O navegador bloqueia áudio até haver um gesto do usuário. Quem recarrega
   * a página com sessão ativa nunca passa pela tela de login — e ficava sem
   * som, em silêncio, sem nada avisando. A cozinha confiava num bipe que não
   * ia tocar.
   *
   * `once: true`: um gesto basta, e o ouvinte se remove sozinho.
   */
  useEffect(() => {
    if (ligado) return
    const religar = () => {
      if (!ctx.current) ctx.current = new AudioContext()
      void ctx.current.resume().then(() => setLigado(ctx.current?.state === 'running'))
    }
    window.addEventListener('pointerdown', religar, { once: true })
    window.addEventListener('keydown', religar, { once: true })
    return () => {
      window.removeEventListener('pointerdown', religar)
      window.removeEventListener('keydown', religar)
    }
  }, [ligado])

  const tocar = () => {
    const c = ctx.current
    if (!c || c.state !== 'running') return
    // Duas notas curtas: corta o barulho da cozinha melhor que um bipe longo.
    for (const [quando, hz] of [[0, 880], [0.18, 1320]] as const) {
      const osc = c.createOscillator()
      const vol = c.createGain()
      osc.type = 'square'
      osc.frequency.value = hz
      vol.gain.setValueAtTime(0.0001, c.currentTime + quando)
      vol.gain.exponentialRampToValueAtTime(0.25, c.currentTime + quando + 0.02)
      vol.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + quando + 0.16)
      osc.connect(vol).connect(c.destination)
      osc.start(c.currentTime + quando)
      osc.stop(c.currentTime + quando + 0.18)
    }
  }

  return { ligado, ativar, tocar }
}
